// Shared formatting for subscription state, used by the subscribe flow and
// the account settings panel so both describe a plan the same way.

export function formatDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export function formatPrice(cents, currency = "usd") {
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency.toUpperCase(),
        // $99.00 reads as clutter; whole-dollar prices show as $99
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
}

/** "3 months" / "12 months" / "45 days" — trials are quoted in days by Stripe. */
export function formatTrialLength(days) {
    if (!days) return null;
    if (days % 30 === 0 || days >= 60) {
        const months = Math.round(days / 30.44);
        if (months >= 1) return `${months} month${months === 1 ? "" : "s"}`;
    }
    return `${days} day${days === 1 ? "" : "s"}`;
}

/** Chip label + MUI colour for a Stripe subscription status. */
export function statusChip(subscription) {
    if (!subscription?.status) {
        return { label: "No subscription", color: "default" };
    }
    switch (subscription.status) {
        case "trialing":
            return { label: "Free trial", color: "info" };
        case "active":
            return subscription.cancelAtPeriodEnd
                ? { label: "Cancels at period end", color: "warning" }
                : { label: "Active", color: "success" };
        case "past_due":
            return { label: "Payment past due", color: "warning" };
        case "canceled":
            return { label: "Canceled", color: "default" };
        case "unpaid":
            return { label: "Unpaid", color: "error" };
        case "incomplete":
        case "incomplete_expired":
            return { label: "Incomplete", color: "warning" };
        default:
            return { label: subscription.status, color: "default" };
    }
}

/**
 * The one line that tells a user what happens next and when.
 * Returns null when there's nothing meaningful to say.
 */
export function renewalSummary(subscription, priceLabel) {
    if (!subscription?.status) return null;

    const trialEnd = formatDate(subscription.trialEnd);
    const periodEnd = formatDate(subscription.currentPeriodEnd);

    if (subscription.status === "trialing" && trialEnd) {
        return subscription.cancelAtPeriodEnd
            ? `Your free trial ends ${trialEnd} and your plan will not renew.`
            : `Free until ${trialEnd}, then ${priceLabel} per year.`;
    }
    if (subscription.status === "active" && periodEnd) {
        return subscription.cancelAtPeriodEnd
            ? `Your plan ends ${periodEnd}. You'll keep access until then.`
            : `Renews ${periodEnd} at ${priceLabel} per year.`;
    }
    if (subscription.status === "past_due") {
        return "We couldn't charge your card. Update your payment method to keep your access.";
    }
    if (subscription.status === "canceled" && periodEnd) {
        return `Your plan ended ${periodEnd}.`;
    }
    return null;
}
